import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Input, Page } from "@theg1group/ui";
import { useState } from "react";
import { BarChart } from "../components/BarChart.tsx";
import { api } from "../lib/api.ts";
import { TimeframeSelect, useTimeframe } from "../lib/timeframe.tsx";

type Tab = "campaign" | "evolution";

export function Timeline() {
	const [tab, setTab] = useState<Tab>("campaign");
	return (
		<Page
			title="Timeline"
			description="Campaign waves and how a domain's infrastructure changed over time."
			actions={tab === "campaign" ? <TimeframeSelect /> : undefined}
		>
			<div className="flex gap-2 mb-3.5">
				<TabBtn active={tab === "campaign"} onClick={() => setTab("campaign")}>
					Campaign volume
				</TabBtn>
				<TabBtn active={tab === "evolution"} onClick={() => setTab("evolution")}>
					Infrastructure evolution
				</TabBtn>
			</div>
			{tab === "campaign" ? <Campaign /> : <Evolution />}
		</Page>
	);
}

function Campaign() {
	const { qs } = useTimeframe();
	const { data } = useQuery({
		queryKey: ["campaign-timeline", qs],
		queryFn: () =>
			api.get<{ buckets: Array<{ day: string; esp: string; count: number }> }>(
				`/api/stats/campaign-timeline?${qs}`,
			),
	});
	const byDay = new Map<string, number>();
	for (const b of data?.buckets ?? []) byDay.set(b.day, (byDay.get(b.day) ?? 0) + b.count);
	const series = [...byDay.entries()].map(([label, value]) => ({ label, value }));

	return (
		<Card>
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-3">Emails per day</h2>
				<BarChart data={series} />
				<div className="flex justify-between text-[11px] text-muted mt-1">
					<span>{series[0]?.label}</span>
					<span>{series[series.length - 1]?.label}</span>
				</div>
			</CardBody>
		</Card>
	);
}

function Evolution() {
	const [domain, setDomain] = useState("");
	const [active, setActive] = useState("");
	const { data } = useQuery({
		queryKey: ["evolution", active],
		queryFn: () =>
			api.get<{ events: Array<{ ts: string; type: string; value: string; name: string }> }>(
				`/api/stats/evolution?domain=${encodeURIComponent(active)}`,
			),
		enabled: !!active,
	});

	return (
		<Card>
			<CardBody>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						setActive(domain.trim().toLowerCase());
					}}
					className="flex gap-2 mb-3 items-start"
				>
					<Input
						value={domain}
						onChange={(e) => setDomain(e.target.value)}
						placeholder="domain (e.g. neopokies.com)"
						className="flex-1 max-w-xs"
					/>
					<Button type="submit" variant="primary">
						Show
					</Button>
				</form>
				{!active && <div className="text-xs text-muted">Enter a domain to see its DNS/infra change history.</div>}
				{active && !data?.events.length && <div className="text-xs text-muted">No recorded changes yet.</div>}
				<ol className="relative border-l border-line ml-2">
					{(data?.events ?? []).map((ev, i) => (
						<li key={`${ev.ts}:${ev.type}:${i}`} className="ml-4 mb-3">
							<div className="absolute w-2 h-2 rounded-full bg-primary -left-1 mt-1.5" />
							<div className="text-[11px] text-muted">{new Date(ev.ts).toLocaleString()}</div>
							<div className="text-sm text-content">
								<Badge variant="info">{ev.type}</Badge>{" "}
								<span className="font-mono text-xs break-all">{ev.value}</span>
							</div>
						</li>
					))}
				</ol>
			</CardBody>
		</Card>
	);
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`px-3 py-1.5 text-sm rounded-md ${
				active ? "bg-primary text-white" : "bg-surface border border-line text-muted hover:bg-surface-2"
			}`}
		>
			{children}
		</button>
	);
}
